import { jwtDecode } from "jwt-decode";
import type { UserRoleApiPayload } from "@/services/communityUserRole";

export type CommunityRole = "admin" | "member";

export interface CommunitySession {
  isAuthenticated: boolean;
  role: CommunityRole;
  email?: string;
}

const COMMUNITY_SESSION_KEY = "cm.community.session";

const isBrowser = typeof window !== "undefined";

const isRole = (value: unknown): value is CommunityRole =>
  value === "admin" || value === "member";

export const getCommunitySession = (): CommunitySession | null => {
  if (!isBrowser) {return null;}

  const rawSession = window.localStorage.getItem(COMMUNITY_SESSION_KEY);
  if (!rawSession) {return null;}

  try {
    const parsed = JSON.parse(rawSession) as Partial<CommunitySession>;
    if (!parsed?.isAuthenticated || !isRole(parsed.role)) {return null;}
    return {
      isAuthenticated: true,
      role: parsed.role,
      email: parsed.email,
    };
  } catch {
    return null;
  }
};

export const setCommunitySession = (role: CommunityRole, email?: string) => {
  if (!isBrowser) {return;}

  const payload: CommunitySession = {
    isAuthenticated: true,
    role,
    email,
  };
  window.localStorage.setItem(COMMUNITY_SESSION_KEY, JSON.stringify(payload));
};

export const clearCommunitySession = () => {
  if (!isBrowser) {return;}
  window.localStorage.removeItem(COMMUNITY_SESSION_KEY);
};

export const getHomeRouteForRole = (role: CommunityRole): string =>
  role === "admin" ? "/admin" : "/member";

export const getRoleForPath = (pathname: string): CommunityRole | null => {
  if (pathname.startsWith("/admin")) {return "admin";}
  if (pathname.startsWith("/member")) {return "member";}
  return null;
};

export const getRoleFromUserType = (
  userType: string | null | undefined
): CommunityRole | null => {
  if (!userType) {return null;}

  const normalized = userType.toLowerCase();
  if (
    normalized.includes("admin") ||
    normalized.includes("super") ||
    normalized.includes("staff")
  ) {
    return "admin";
  }

  if (
    normalized.includes("member") ||
    normalized.includes("user") ||
    normalized.includes("volunteer")
  ) {
    return "member";
  }

  return null;
};

/**
 * Maps GET user-role API body to portal role. Prefer `role_name` when present
 * (e.g. "Community Admin", "Super Admin" → admin; "user" → member).
 */
export const getCommunityRoleFromUserRoleApiPayload = (
  payload: UserRoleApiPayload | null | undefined
): CommunityRole | null => {
  if (!payload) {
    return null;
  }
  const roleNameRaw =
    payload.role_name ?? payload.data?.role_name ?? "";
  const roleName = String(roleNameRaw).trim().toLowerCase();
  if (roleName === "community admin" || roleName === "super admin") {
    return "admin";
  }
  if (roleName === "user") {
    return "member";
  }
  if (roleName) {
    const inferred = getRoleFromUserType(roleName);
    if (inferred) {
      return inferred;
    }
  }
  return (
    getRoleFromUserType(payload.community_role) ??
    getRoleFromUserType(payload.user_type) ??
    getRoleFromUserType(payload.role) ??
    getRoleFromUserType(payload.data?.community_role) ??
    getRoleFromUserType(payload.data?.user_type) ??
    getRoleFromUserType(payload.data?.role) ??
    null
  );
};

export const sanitizeRedirectPath = (path: string | null | undefined): string => {
  if (!path) {return "/";}
  return path.startsWith("/") ? path : "/";
};

export const resolveRoleFromRedirect = (
  redirectPath: string,
  fallbackRole: CommunityRole = "member"
): CommunityRole => getRoleForPath(redirectPath) ?? fallbackRole;

/** Reads user id from JWT access payload (login APIs vary: `user_id`, `userId`, `sub`, `id`). */
export const getUserIdFromAccessToken = (token: string): string => {
  const trimmed = token.trim();
  if (!trimmed) {
    return "";
  }
  try {
    const decoded = jwtDecode<Record<string, unknown>>(trimmed);
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        const v = decoded[key];
        const s = typeof v === "string" ? v.trim() : "";
        if (s) {
          return s;
        }
      }
      return "";
    };
    return pick("user_id", "userId", "sub", "id");
  } catch {
    return "";
  }
};

export const getPostAuthRedirect = (
  redirectPath: string,
  role: CommunityRole
): string => {
  const safePath = sanitizeRedirectPath(redirectPath);
  const requestedRole = getRoleForPath(safePath);

  if (!requestedRole || requestedRole === role) {
    return safePath === "/" ? getHomeRouteForRole(role) : safePath;
  }

  return getHomeRouteForRole(role);
};
